import { Link } from "react-router-dom";
import { useAdmin } from "../../contexts/AdminContext";

const navCols = [
  {
    title: "Verlosung",
    links: [
      { label: "Die Villa", to: "/bien" },
      { label: "Pakete & Lose", to: "/forfaits" },
      { label: "Mein Konto", to: "/compte" },
      { label: "Guter Zweck", to: "/#guter-zweck" },
    ],
  },
  {
    title: "Info & Rechtliches",
    links: [
      { label: "Datenschutz", to: "/mentions-legales" },
      { label: "Impressum", to: "/mentions-legales" },
      { label: "AGB", to: "/mentions-legales" },
      { label: "Hilfe & FAQ", to: "/contact" },
      { label: "Spielerschutz", to: "/mentions-legales" },
    ],
  },
  {
    title: "Über uns",
    links: [
      { label: "Kontakt", to: "/#contact" },
      { label: "Wie es funktioniert", to: "/comment" },
      { label: "Bekannt aus", to: "/" },
      { label: "Presse", to: "#" },
    ],
  },
];

export default function Footer() {
  const { settings } = useAdmin();

  const getIcon = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes("instagram")) return "ri-instagram-line";
    if (t.includes("facebook")) return "ri-facebook-fill";
    if (t.includes("tiktok")) return "ri-tiktok-fill";
    if (t.includes("youtube")) return "ri-youtube-fill";
    if (t.includes("twitter") || t.includes(" x ")) return "ri-twitter-x-line";
    return "ri-share-line";
  };

  const socials = settings.socials.map(s => ({
    ...s,
    icon: getIcon(s.title)
  }));

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-10">

        {/* ── TOP: Logo + Description + Socials ── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">

          {/* Brand block */}
          <div className="lg:col-span-1 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="w-0.5 h-8 bg-white/60 flex-shrink-0"></div>
              <img
                src={settings.logo}
                alt={settings.siteName}
                className="h-7 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Die zertifizierte Immobilien-Verlosung — gewinnen Sie Ihre Traumvilla mit {settings.siteName} und unterstützen Sie einen guten Zweck.
            </p>

            {/* Trustpilot */}
            <a
              href={`https://fr.trustpilot.com/review/${settings.siteName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col gap-2 cursor-pointer group w-fit"
            >
              {/* Stars row */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-px">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-5 h-5 bg-[#00b67a] flex items-center justify-center flex-shrink-0">
                      <i className="ri-star-fill text-white" style={{ fontSize: 10 }}></i>
                    </div>
                  ))}
                  {/* 5e étoile à 60% */}
                  <div className="w-5 h-5 flex-shrink-0 relative overflow-hidden bg-[#dcdce6]">
                    <div className="absolute inset-0 bg-[#00b67a]" style={{ width: "60%" }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-star-fill text-white" style={{ fontSize: 10 }}></i>
                    </div>
                  </div>
                </div>
                <span className="text-white text-xs font-bold">4.6 / 5</span>
              </div>

              {/* Logo Trustpilot recréé */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  {/* Icône étoile verte */}
                  <div className="w-[18px] h-[18px] bg-[#00b67a] flex items-center justify-center flex-shrink-0" style={{ clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 2% 35%, 39% 35%)" }}>
                  </div>
                  <span className="text-white font-semibold text-sm tracking-tight leading-none" style={{ fontFamily: "sans-serif" }}>
                    Trustpilot
                  </span>
                </div>
                <span className="text-slate-500 text-xs">806 Bewertungen</span>
              </div>
            </a>

            {/* Social icons */}
            <div className="flex items-center gap-2.5">
              {socials.map((s) => (
                <a
                  key={s.title}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Nav columns */}
          {navCols.map((col) => (
            <div key={col.title}>
              <p className="text-white font-bold text-sm mb-5 uppercase tracking-wider">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-slate-400 hover:text-white text-sm transition-colors cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── MIDDLE: Payment logos ── */}
        <div className="flex flex-wrap items-center justify-center gap-3 py-8 border-b border-white/10">

          {/* PayPal */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-white px-3 gap-0.5">
            <span className="font-extrabold text-[#003087] text-sm leading-none">Pay</span>
            <span className="font-extrabold text-[#009cde] text-sm leading-none">Pal</span>
          </div>

          {/* SEPA */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-white px-3">
            <div className="flex flex-col items-center leading-none">
              <span className="font-bold text-[#003399] text-xs tracking-widest">SEPA</span>
              <span className="text-slate-400 text-[8px] tracking-wide mt-0.5">Lastschrift</span>
            </div>
          </div>

          {/* Visa */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-white px-3 gap-2">
            <span className="font-extrabold italic text-[#1a1f71] text-sm leading-none">VISA</span>
            <div className="flex items-center">
              <div className="w-5 h-5 rounded-full bg-[#eb001b]"></div>
              <div className="w-5 h-5 rounded-full bg-[#f79e1b] -ml-2.5 opacity-90"></div>
            </div>
          </div>

          {/* Klarna */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-[#ffb3c7] px-3">
            <span className="font-black text-[#17120f] text-sm tracking-tight leading-none">klarna</span>
          </div>

          {/* Apple Pay */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-black px-3 gap-1.5 border border-white/10">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-white flex-shrink-0">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.53 3.99zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <span className="font-semibold text-white text-xs leading-none">Pay</span>
          </div>

          {/* Google Pay */}
          <div className="flex h-10 items-center justify-center rounded-lg bg-white px-3 gap-px">
            <span className="font-bold text-xs leading-none" style={{ color: "#4285F4" }}>G</span>
            <span className="font-bold text-xs leading-none" style={{ color: "#EA4335" }}>o</span>
            <span className="font-bold text-xs leading-none" style={{ color: "#FBBC05" }}>o</span>
            <span className="font-bold text-xs leading-none" style={{ color: "#4285F4" }}>g</span>
            <span className="font-bold text-xs leading-none" style={{ color: "#34A853" }}>l</span>
            <span className="font-bold text-xs leading-none" style={{ color: "#EA4335" }}>e</span>
            <span className="font-semibold text-slate-700 text-xs leading-none ml-1">Pay</span>
          </div>

          <div className="h-6 w-px bg-white/10 mx-2 hidden sm:block"></div>

          {/* GGL Logo (Footer Version) */}
          <div className="flex flex-col items-center gap-1 cursor-default">
            <div className="flex items-center gap-1.5 h-10 px-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex flex-col items-center leading-none">
                <span className="font-black text-white text-[10px] tracking-tight leading-none uppercase">GGL</span>
                <span className="text-white/40 text-[5px] tracking-widest leading-none mt-0.5">BEHÖRDE</span>
              </div>
              <div className="w-px h-4 bg-white/20 mx-0.5"></div>
              <div className="flex flex-col items-start leading-none gap-0.5">
                <span className="text-[5px] text-white/30 leading-tight uppercase font-bold">Gemeinsame</span>
                <span className="text-[5px] text-white/30 leading-tight uppercase font-bold">Glücksspiel</span>
              </div>
            </div>
            <span className="text-white/30 text-[8px] font-bold uppercase tracking-wider">GGL Zugelassen</span>
          </div>

          {/* TÜV Logo (Footer Version) */}
          <div className="flex flex-col items-center gap-1 cursor-default ml-2">
            <div className="flex items-center gap-2 h-10 px-3 bg-white/5 rounded-lg border border-white/10">
              <div className="w-6 h-6 rounded-full border border-white/40 flex items-center justify-center flex-shrink-0 bg-white/5">
                <span className="font-black text-white text-[8px] leading-none tracking-tighter uppercase">TÜV</span>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[5px] text-white/40 uppercase font-black tracking-tight">Geprüfte</span>
                <span className="text-[5px] font-black text-white tracking-tight uppercase leading-none">Sicherheit</span>
              </div>
            </div>
            <span className="text-white/30 text-[8px] font-bold uppercase tracking-wider">TÜV Geprüft</span>
          </div>
        </div>

        {/* ── BOTTOM: 18+ disclaimer + legal ── */}
        <div className="pt-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6 mb-6">
            {/* 18+ */}
            <div className="flex items-start gap-3 flex-1">
              <div className="w-8 h-8 rounded-full border-2 border-white/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white/70 font-extrabold text-[9px] leading-none">18+</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed">
                Spielen mit Verantwortung. <strong className="text-white/70">Spielteilnahme ab 18 Jahren.</strong> Glücksspiel kann süchtig machen.
              </p>
            </div>

            {/* Copyright */}
            <div className="text-right text-xs text-white/30">
              <p>© {new Date().getFullYear()} {settings.companyName}</p>
              <p className="mt-1">{settings.contactAddress}</p>
              <p className="mt-1">GGL Lizenz 2024</p>
            </div>
          </div>

          {/* Legal text */}
          <p className="text-xs text-white/30 leading-relaxed border-t border-white/5 pt-5">
            Zertifizierte Immobilien-Verlosung. Alle Rechte vorbehalten.
          </p>
        </div>

      </div>
    </footer>
  );
}
